import ResizableScene from "../core/ResizableScene";
import GameScene from "../scenes/GameScene";
import ArrayUtils from "../utils/ArrayUtils";
import { MapObject, Tile } from "../utils/TiledMap";
import { Component } from "./Component";

export class GameObject extends Phaser.GameObjects.Container {
	scene: GameScene;

	components: Component[];

	constructor(scene: ResizableScene) {
		super(scene);
		this.components = [];
	}

	destroy(): void {
		for (const component of this.components) component.destroy();
		super.destroy();
	}

	update(dt: number): void {
		for (const component of this.components) {
			if (component.enabled) component.update(dt);
		}
	}

	physicsUpdate(dt: number): void {
		for (const component of this.components) {
			if (component.enabled) component.physicsUpdate(dt);
		}
	}

	addComponent<T extends Component>(component: T): T {
		this.components.push(component);
		return component;
	}

	removeComponent<T extends Component>(component: T): void {
		component.destroy();
		ArrayUtils.remove(this.components, component);
	}

	getComponent<T extends new (...args: any) => InstanceType<T>>(Type: T): InstanceType<T> {
		return this.components.find((c) => c instanceof Type) as InstanceType<T>;
	}

	// creates an image on a game object with an origin point and hitbox defined by a tile in a tileset
	createObjectFromTile(scene: ResizableScene, obj: MapObject, tile: Tile, img: string): Phaser.GameObjects.Image {
		const image = scene.add.image(0, 0, img);
		image.setOrigin(0, 0);
		this.add(image);

		if (tile.objectgroup) {
			const origin = tile.objectgroup.objects.find((o) => o.point && o.type == "origin");
			if (origin) {
				const x = origin.x / image.width;
				const y = origin.y / image.height;
				image.setOrigin(x, y);
				this.x += x * image.width;
				this.y += y * image.height;
			} else {
				const x = 0.5;
				const y = 0;
				image.setOrigin(x, y);
				this.x += x * image.width;
				this.y += y * image.height;
			}
		}

		if (obj.flipX != null) {
			this.scaleX = -1;
			this.x += (0.5 - image.originX) * image.width * 2;
		}

		// if (tile.objectgroup) {
		// 	const hitboxes = tile.objectgroup.objects.filter((o) => o.type == "hitbox");
		// 	for (const hitbox of hitboxes) {
		// 		const x = this.x - image.originX * image.width;
		// 		const y = this.y - image.originY * image.height;
		// 	}
		// }

		return image;
	}

	addImage(key: string): Phaser.GameObjects.Image {
		const img = this.scene.add.image(0, 0, key);
		this.add(img);
		return img;
	}

	addSprite(key: string): Phaser.GameObjects.Sprite {
		const sprite = this.scene.add.sprite(0, 0, key);
		this.add(sprite);
		return sprite;
	}

	// transform this object's position into world space
	getOurWorldPosition(): Phaser.Math.Vector2 {
		if (this.parentContainer != null) {
			return this.parentContainer
				.getLocalTransformMatrix()
				.transformPoint(this.x, this.y, new Phaser.Math.Vector2()) as Phaser.Math.Vector2;
		} else return new Phaser.Math.Vector2(this.x, this.y);
	}

	// gets world position of a point within this container
	getWorldPosition(x: number, y: number): Phaser.Math.Vector2 {
		return this.getLocalTransformMatrix().transformPoint(x, y, new Phaser.Math.Vector2()) as Phaser.Math.Vector2;
	}

	// transforms world position into same container as us
	getLocalPosition(x = this.x, y = this.y): Phaser.Math.Vector2 {
		return this.parentContainer.getLocalTransformMatrix().applyInverse(x, y, new Phaser.Math.Vector2()) as Phaser.Math.Vector2;
	}

	// transforms world position into position inside us
	getInternalPosition(x: number, y: number): Phaser.Math.Vector2 {
		return this.getLocalTransformMatrix().applyInverse(x, y, new Phaser.Math.Vector2()) as Phaser.Math.Vector2;
	}

	onPause(): void {
		// override
	}

	onResume(): void {
		// override
	}
}
